import { useEffect, useMemo, useState } from "react";
import projectApi from '../../../../utils/api/project';
import { Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import RightContactsPanel from "./RightContactsPanel";

interface UserItem {
  id: number;
  name: string;
  status?: 'on' | 'off';
  is_active?: boolean;
}

interface NotificationType {
  id: number;
  name: string;
  is_enabled?: boolean;
  users?: UserItem[];
}

interface NotificationsUsersList {
  notificationTypes: NotificationType[];
}

const NotificationsProjectSetting = ({ projectId, onChange }: any) => {
  const [notificationCollapse, setNotificationCollapse] = useState(true);
  const [notificationsUsersList, setNotificationsUsersList] = useState<Partial<NotificationsUsersList>>({});
  const [openTypeId, setOpenTypeId] = useState<number | null>(null);
  const [typeEnabled, setTypeEnabled] = useState<Record<number, boolean>>({});
  const [userEnabledByType, setUserEnabledByType] = useState<Record<number, Record<number, boolean>>>({});
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showRightOnMobile, setShowRightOnMobile] = useState<boolean>(false);

  const notificationsUsersListFallback: NotificationsUsersList = {
    notificationTypes: [],
  };

  const activeList: NotificationsUsersList = useMemo(() => {
    const hasApiData =
      notificationsUsersList &&
      Array.isArray(notificationsUsersList.notificationTypes) &&
      (notificationsUsersList.notificationTypes as NotificationType[]).length > 0;

    return hasApiData
      ? (notificationsUsersList as NotificationsUsersList)
      : notificationsUsersListFallback;
  }, [notificationsUsersList]);

  useEffect(() => {
    async function fetchNotificationsUsersList() {
      if (!projectId) return;

      try {
        const resp = await projectApi.notificationsUsersList(projectId);

        setNotificationsUsersList(resp || {});
      } catch (e) {
        setNotificationsUsersList({});
      }
    }

    fetchNotificationsUsersList();
  }, [projectId]);

  useEffect(() => {
    const nextTypeEnabled: Record<number, boolean> = {};
    const nextUserEnabledByType: Record<number, Record<number, boolean>> = {};

    (activeList.notificationTypes || []).forEach((nt) => {
      nextTypeEnabled[nt.id] = !!nt.is_enabled;
      const perUser: Record<number, boolean> = {};

      (nt.users || []).forEach((u) => {
        perUser[u.id] = typeof u.is_active === 'boolean' ? !!u.is_active : u.status === 'on';
      });
      nextUserEnabledByType[nt.id] = perUser;
    });

    setTypeEnabled(nextTypeEnabled);
    setUserEnabledByType(nextUserEnabledByType);
    emitChange(nextTypeEnabled, nextUserEnabledByType);
    const firstTypeId = activeList.notificationTypes?.[0]?.id ?? null;

    setOpenTypeId(firstTypeId);
  }, [activeList.notificationTypes]);

  const emitChange = (nextTypeEnabled: Record<number, boolean>, nextUserEnabledByType: Record<number, Record<number, boolean>>) => {
    try {
      onChange && onChange({
        types: nextTypeEnabled,
        userStatusByType: nextUserEnabledByType,
      });
    } 
    catch (_) {}
  };

  const handleNotificationToggle = (typeId: number) => {
    setTypeEnabled((prev) => {
      const updated = { ...prev, [typeId]: !prev[typeId] };

      emitChange(updated, userEnabledByType);

      return updated;
    }); 
  };

  
  const handleRowClick = (typeId: number) => {
    setOpenTypeId(prev => {
      const isSame = prev === typeId;

      if (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) {
        setShowRightOnMobile(isSame ? !showRightOnMobile : true);
      }
      
      return typeId;
    });
  };

  return (
    <div className={`notifications-project-setting ${showRightOnMobile ? 'notifications-project-setting--show-right' : ''}`}>
      <div
        className="notifications-project-setting__header"
        onClick={() => setNotificationCollapse(!notificationCollapse)}
      >
        <h3>Notifications</h3>
        <i
          className={notificationCollapse ? "fa fa-chevron-down" : "fa fa-chevron-up"}
          aria-hidden="true"
        />
      </div>
      <hr />

      {notificationCollapse && (
        <div className="notifications-project-setting__container">
          <div className="notifications-project-setting__left">
            {(activeList.notificationTypes || []).map((type) => {
              const isActive = openTypeId === type.id;
              const currentType = activeList.notificationTypes?.find(t => t.id === type.id) || null;

              return (
                <div key={type.id} className={`notifications-project-setting__item ${isActive ? 'active' : ''}`}>
                  <div
                    className={`notifications-project-setting__row ${isActive ? 'active' : ''}`}
                    onClick={() => handleRowClick(type.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleRowClick(type.id);
                    }}
                  >
                    {/* Mobile-only dropdown toggle target area on the far left */}
                    <span style={{display: "flex", gap: "5px"}}>
                      {type.name}
                      <Tooltip
                        title='Disabling this notification type will prevent any notifications from being sent for this project. Individual user settings will be preserved.'
                        arrow
                        enterTouchDelay={0}
                      >
                        <span><FontAwesomeIcon icon="question-circle" size="sm" color="#0d2330" /></span>
                      </Tooltip>
                    </span>
                    <label
                      className="switch"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!typeEnabled[type.id]}
                        onChange={() => handleNotificationToggle(type.id)}
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>

                  {/* Mobile-only right content for this specific type */}
                  <div className={`notifications-project-setting__right notifications-project-setting__right--mobile ${isActive && showRightOnMobile ? 'show' : ''}`}>
                    <RightContactsPanel
                      type={currentType}
                      typeEnabled={!!typeEnabled[type.id]}
                      userEnabled={userEnabledByType[type.id] || {}}
                      onToggleAll={(next) => {
                        if (!currentType) return;
                        setUserEnabledByType(prev => {
                          const prevType = prev[type.id] || {};
                          const updatedType: Record<number, boolean> = {};

                          (currentType.users || []).forEach(u => { updatedType[u.id] = next; });
                          const updated = { ...prev, [type.id]: { ...prevType, ...updatedType } };

                          emitChange(typeEnabled, updated);
                          
                          return updated;
                        });
                      }}
                      onToggleUser={(userId) => {
                        setUserEnabledByType(prev => {
                          const prevType = prev[type.id] || {};
                          const updated = {
                            ...prev,
                            [type.id]: {
                              ...prevType,
                              [userId]: !prevType[userId],
                            },
                          };

                          emitChange(typeEnabled, updated);

                          return updated;
                        });
                      }}
                      searchQuery={searchQuery}
                      onSearchChange={(val) => setSearchQuery(val)}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop spacer for seamless gap color */}
          <div className="notifications-project-setting__spacer" aria-hidden="true" />

          {/* Global desktop right panel */}
          <div className="notifications-project-setting__right notifications-project-setting__right--desktop">
            <RightContactsPanel
              type={activeList.notificationTypes?.find(t => t.id === openTypeId) || null}
              typeEnabled={openTypeId != null ? !!typeEnabled[openTypeId] : false}
              userEnabled={openTypeId != null ? (userEnabledByType[openTypeId] || {}) : {}}
              onToggleAll={(next) => {
                if (openTypeId == null) return;
                const selType = activeList.notificationTypes?.find(t => t.id === openTypeId);

                if (!selType) return;
                setUserEnabledByType(prev => {
                  const prevType = prev[openTypeId] || {};
                  const updatedType: Record<number, boolean> = {};

                  (selType.users || []).forEach(u => { updatedType[u.id] = next; });
                  const updated = { ...prev, [openTypeId]: { ...prevType, ...updatedType } };

                  emitChange(typeEnabled, updated);

                  return updated;
                });
              }}
              onToggleUser={(userId) => {
                if (openTypeId == null) return;
                setUserEnabledByType(prev => {
                  const prevType = prev[openTypeId] || {};
                  const updated = {
                    ...prev,
                    [openTypeId]: {
                      ...prevType,
                      [userId]: !prevType[userId],
                    },
                  };

                  emitChange(typeEnabled, updated);

                  return updated;
                });
              }}
              searchQuery={searchQuery}
              onSearchChange={(val) => setSearchQuery(val)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsProjectSetting;